<template>
  <div class="prelab-data-table">
    <h6 class="fw-semibold mb-2">Data Collection Table</h6>
    <p class="text-muted small mb-2">
      Enter Q (GPM) and your calculated pump head H (ft) for each valve
      position.
    </p>

    <div class="table-responsive">
      <table class="table table-sm table-bordered align-middle mb-2">
        <thead class="table-light">
          <tr>
            <th scope="col" style="width: 40px" class="text-center">#</th>
            <th scope="col">Q (GPM)</th>
            <th scope="col">H (ft)</th>
            <th scope="col" style="width: 50px">
              <span class="visually-hidden">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, i) in rows" :key="i">
            <td class="text-center text-muted">{{ i + 1 }}</td>
            <td>
              <input
                type="number"
                class="form-control form-control-sm"
                step="0.1"
                min="0"
                v-model.number="row.Q"
                @blur="normalizeRowField(row, 'Q')"
                placeholder="e.g. 45.0"
                :aria-label="'Flow rate Q in GPM for row ' + (i + 1)"
              />
            </td>
            <td>
              <input
                type="number"
                class="form-control form-control-sm"
                step="0.1"
                min="0"
                v-model.number="row.H"
                @blur="normalizeRowField(row, 'H')"
                placeholder="e.g. 75.2"
                :aria-label="'Pump head H in feet for row ' + (i + 1)"
              />
            </td>
            <td class="text-center">
              <button
                class="btn btn-sm btn-outline-danger py-0 px-1"
                :disabled="rows.length <= MIN_ROWS"
                @click="removeRow(i)"
                :aria-label="'Remove row ' + (i + 1)"
              >
                <i class="bi bi-x-lg" aria-hidden="true"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <button class="btn btn-sm btn-outline-secondary" @click="addRow">
      <i class="bi bi-plus-circle me-1"></i>Add Row
    </button>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

const MIN_ROWS = 1;
const MIN_VALID_VALUE = 0;

export default {
  name: "PreLabDataTable",
  emits: ["data-changed"],
  data() {
    return {
      MIN_ROWS,
      rows: [{ Q: null, H: null }]
    };
  },
  computed: {
    ...mapGetters(["preLabRows"])
  },
  watch: {
    rows: {
      deep: true,
      handler() {
        this.emitData();
        this.setPreLabRows(JSON.parse(JSON.stringify(this.rows)));
      }
    },
    preLabRows: {
      immediate: true,
      handler(saved) {
        if (!saved || saved.length === 0) return;
        const storeJSON = JSON.stringify(saved);
        const localJSON = JSON.stringify(this.rows);
        if (storeJSON !== localJSON) {
          this.rows = saved.map((r) => ({ Q: r.Q ?? null, H: r.H ?? null }));
        }
      }
    }
  },
  methods: {
    ...mapActions(["setPreLabRows"]),
    normalizeRowField(row, field) {
      const raw = row[field];
      if (raw === null || raw === undefined || raw === "") {
        row[field] = null;
        return;
      }
      if (typeof raw === "string") {
        const trimmed = raw.trim();
        if (!trimmed) {
          row[field] = null;
          return;
        }
        const parsed = Number(trimmed);
        row[field] = Number.isFinite(parsed) ? parsed : null;
        return;
      }
      row[field] = Number.isFinite(raw) ? raw : null;
    },
    addRow() {
      this.rows.push({ Q: null, H: null });
    },
    removeRow(index) {
      if (this.rows.length > MIN_ROWS) {
        this.rows.splice(index, 1);
      }
    },
    emitData() {
      const valid = this.rows.filter(
        (r) =>
          Number.isFinite(r.Q) &&
          Number.isFinite(r.H) &&
          r.Q > MIN_VALID_VALUE &&
          r.H > MIN_VALID_VALUE
      );
      this.$emit("data-changed", valid);
    },
    reset() {
      this.rows = [{ Q: null, H: null }];
    }
  }
};
</script>
